import { useEffect, useRef } from 'react';
import { doc, increment, writeBatch, arrayUnion } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { usePlayerStore } from '../stores/playerStore';
import { safeSetItem } from '../utils/localStorageHelpers';

const UPDATE_INTERVAL = 60 * 1000; // Update every minute
const BATCH_UPDATE_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_WRITES_PER_HOUR = 100;
const MAX_RECENT_STATIONS = 10;

interface LocalListeningData {
  userId: string | null;
  listeningTime: number;
  lastSyncTime: number;
  writesThisHour: number;
  lastWriteReset: number;
  recentlyPlayed: string[];
  exploredStations: Set<string>;
}

export const useListeningStats = () => {
  const { user } = useAuth();
  const { currentStation, isPlaying } = usePlayerStore();
  const localDataRef = useRef<LocalListeningData>({
    userId: null,
    listeningTime: 0,
    lastSyncTime: Date.now(),
    writesThisHour: 0,
    lastWriteReset: Date.now(),
    recentlyPlayed: [],
    exploredStations: new Set()
  });

  // Load local data on mount
  useEffect(() => {
    const storedData = localStorage.getItem('playerData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (parsedData.userId === user?.uid) {
        localDataRef.current = {
          ...parsedData,
          recentlyPlayed: parsedData.recentlyPlayed || [],
          exploredStations: new Set(parsedData.exploredStations || [])
        };
      }
    }
  }, [user?.uid]);

  // Save local data periodically
  useEffect(() => {
    const saveLocalData = () => {
      if (user?.uid) {
        const { exploredStations, ...rest } = localDataRef.current;
        safeSetItem(
          'playerData',
          JSON.stringify({
            ...rest,
            exploredStations: Array.from(exploredStations)
          }),
          {
            context: 'useListeningStats.saveLocalData',
            onQuotaExceeded: () => {
              console.info('useListeningStats.saveLocalData: Clearing stored player data after quota exceed.');
              try {
                localStorage.removeItem('playerData');
              } catch (cleanupError) {
                console.warn(
                  'useListeningStats.saveLocalData: Failed to clear stored player data after quota exceed.',
                  cleanupError
                );
              }
            }
          }
        );
      }
    };

    const interval = setInterval(saveLocalData, UPDATE_INTERVAL);
    window.addEventListener('beforeunload', saveLocalData);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', saveLocalData);
      saveLocalData();
    };
  }, [user?.uid]);

  // Track listening time and update Firestore
  useEffect(() => {
    if (!user?.uid || !isPlaying || !currentStation) return;

    const updateListeningStats = async () => {
      const now = Date.now();
      const timeSinceLastSync = now - localDataRef.current.lastSyncTime;

      // Reset writes counter if an hour has passed
      if (now - localDataRef.current.lastWriteReset > 3600000) {
        localDataRef.current.writesThisHour = 0;
        localDataRef.current.lastWriteReset = now;
      }

      // Check if we've exceeded write limits
      if (localDataRef.current.writesThisHour >= MAX_WRITES_PER_HOUR) {
        return;
      }

      try {
        const batch = writeBatch(db);
        const userStatsRef = doc(db, 'userStats', user.uid);
        const stationStatsRef = doc(db, 'stationStats', currentStation.stationuuid);

        // Update listening time
        batch.update(userStatsRef, {
          totalListeningTime: increment(timeSinceLastSync),
          [`stationTime.${currentStation.stationuuid}`]: increment(timeSinceLastSync)
        });

        // Update station stats
        batch.update(stationStatsRef, {
          totalListeners: increment(1),
          totalListeningTime: increment(timeSinceLastSync)
        });

        // Track recently played stations
        if (!localDataRef.current.recentlyPlayed.includes(currentStation.stationuuid)) {
          batch.update(userStatsRef, {
            recentlyPlayed: arrayUnion(currentStation.stationuuid)
          });
          localDataRef.current.recentlyPlayed.push(currentStation.stationuuid);
          if (localDataRef.current.recentlyPlayed.length > MAX_RECENT_STATIONS) {
            localDataRef.current.recentlyPlayed.shift();
          }
        }

        // Track explored stations
        if (!localDataRef.current.exploredStations.has(currentStation.stationuuid)) {
          batch.update(userStatsRef, {
            exploredStations: increment(1)
          });
          localDataRef.current.exploredStations.add(currentStation.stationuuid);
        }

        await batch.commit();
        localDataRef.current.lastSyncTime = now;
        localDataRef.current.writesThisHour++;

      } catch (error) {
        console.error('Failed to update listening stats:', error);
      }
    };

    const interval = setInterval(updateListeningStats, BATCH_UPDATE_INTERVAL);
    return () => clearInterval(interval);
  }, [user?.uid, isPlaying, currentStation]);

  return null;
}; 